import { useEffect, useState } from "react";
import { signup } from "wasp/client/auth";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthPageLayout } from "./AuthPageLayout";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  clearAffiliateRef,
  getStoredAffiliateRef,
  storeAffiliateRef,
} from "../client/affiliateRef";

export const PENDING_PLAN_KEY = "edgely_pending_plan";

export function Signup() {
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Persist the plan query param so we can redirect to Stripe after email verification + login
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get("plan");
    if (plan) {
      sessionStorage.setItem(PENDING_PLAN_KEY, plan);
    }
    const ref = params.get("ref");
    if (ref) {
      storeAffiliateRef(ref);
    }
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const confirmTouched = confirmPassword !== "";
  const passwordsMatch = password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError(t.auth.firstNameRequired);
      return;
    }

    if (!lastName.trim()) {
      setError(t.auth.lastNameRequired);
      return;
    }

    if (password.length < 8) {
      setError(t.auth.passwordTooShort);
      return;
    }

    if (!passwordsMatch) {
      setError(t.auth.passwordsDoNotMatch);
      return;
    }

    setIsLoading(true);
    try {
      const utmSource = localStorage.getItem('edgely_utm_source') ?? undefined;
      const ambassadorRef = getStoredAffiliateRef() ?? undefined;
      // username is derived from email server-side in userSignupFields.ts
      await signup({ email, password, firstName: firstName.trim(), lastName: lastName.trim(), utmSource, ambassadorRef } as any);
      localStorage.removeItem('edgely_utm_source');
      clearAffiliateRef();
      navigate(routes.EmailVerificationRoute.to, { state: { email } });
    } catch (err: any) {
      setError(err?.message || t.auth.signupError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <form onSubmit={handleSubmit} className="space-y-4 pt-8">
        <div className="flex gap-3">
          <div className="space-y-1 flex-1">
            <Label htmlFor="firstName" className="text-gray-700 dark:text-gray-700">
              {t.auth.firstName}
            </Label>
            <Input
              id="firstName"
              type="text"
              required
              autoComplete="given-name"
              placeholder={t.auth.firstNamePlaceholder}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="bg-white text-gray-900 border-gray-300"
            />
          </div>
          <div className="space-y-1 flex-1">
            <Label htmlFor="lastName" className="text-gray-700 dark:text-gray-700">
              {t.auth.lastName}
            </Label>
            <Input
              id="lastName"
              type="text"
              required
              autoComplete="family-name"
              placeholder={t.auth.lastNamePlaceholder}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="bg-white text-gray-900 border-gray-300"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="email" className="text-gray-700 dark:text-gray-700">
            {t.auth.email}
          </Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            placeholder={t.auth.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white text-gray-900 border-gray-300"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="password" className="text-gray-700 dark:text-gray-700">
            {t.auth.password}
          </Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            placeholder={t.auth.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-white text-gray-900 border-gray-300"
          />
          <p className="text-xs text-gray-500 mt-1">{t.auth.passwordMinLength}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="confirmPassword" className="text-gray-700 dark:text-gray-700">
            {t.auth.confirmPassword}
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            required
            autoComplete="new-password"
            placeholder={t.auth.confirmPasswordPlaceholder}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`bg-white text-gray-900 ${
              confirmTouched && !passwordsMatch
                ? "border-red-500 focus-visible:ring-red-500"
                : confirmTouched && passwordsMatch
                  ? "border-green-500 focus-visible:ring-green-500"
                  : "border-gray-300"
            }`}
          />
          {confirmTouched && (
            <p className={`text-xs mt-1 ${passwordsMatch ? "text-green-600" : "text-red-500"}`}>
              {passwordsMatch ? t.auth.passwordsMatch : t.auth.passwordsDoNotMatch}
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <Button
          type="submit"
          disabled={isLoading || (confirmTouched && !passwordsMatch)}
          className="w-full"
          style={{ backgroundColor: "hsl(var(--primary))" }}
        >
          {isLoading ? t.auth.signingUp : t.auth.signup}
        </Button>
      </form>

      <br />
      <span className="text-sm font-medium text-gray-900">
        {t.auth.ifEverythingOk}{" "}
        <WaspRouterLink to={routes.LoginRoute.to} className="underline">
          {t.auth.goToLogin}
        </WaspRouterLink>
      </span>
      <br />
    </AuthPageLayout>
  );
}
